import PropTypes from "prop-types";
import { Loader, Modal } from "rsuite";
import {
  AREA_BADGES,
  actionDescription,
  aggregateChangeFields,
  buildAggregatedHeaderRows,
  buildDetailInfoRows,
  formatValue,
  isOutcomeFormat,
  isWholeValueFormat,
  resolveFieldFormat,
} from "./utils";
import {
  DetailSection,
  DiffRow,
  InfoGrid,
  ListValue,
  ValueRow,
} from "./ActivityDetailSections";

// Colored area pill — same tokens as the results-table Type badge.
const AreaBadge = ({ area }) => {
  if (!area) return null;
  const cfg = AREA_BADGES[area];
  return (
    <span
      className={`activity-type-badge ${cfg ? cfg.className : "badge-default"}`}
    >
      {cfg ? cfg.label : area}
    </span>
  );
};

AreaBadge.propTypes = { area: PropTypes.string };

const hasValue = (v) => v !== null && v !== undefined && v !== "";

// A date_range value is a single [from, to] pair, except in the aggregated "Applied To"
// block, where non-contiguous dates arrive as several pairs.
const isRangeList = (raw) =>
  Array.isArray(raw) && raw.length > 1 && raw.every(Array.isArray);

/**
 * A multi-entry list renders collapsibly rather than as one joined string, so a row
 * holding fifty properties stays one line until asked to open. Everything else goes
 * through formatValue, which owns the boolean/number/date wording.
 */
const renderValue = (raw, format, tone) => {
  if (format === "date_range") {
    // Each pair is formatted first, so the list collapses on ranges rather than on the
    // individual dates that make them up.
    return isRangeList(raw) ? (
      <ListValue items={raw.map((r) => formatValue(r, format))} tone={tone} />
    ) : (
      formatValue(raw, format)
    );
  }
  // The formatter owns this value whole — a composed set of parts, or text it parses
  // itself — so it must reach the formatter rather than be collapsed into a chip list.
  if (isWholeValueFormat(format)) {
    return formatValue(raw, format);
  }
  return Array.isArray(raw) && raw.length > 1 ? (
    <ListValue items={raw} tone={tone} />
  ) : (
    formatValue(raw, format)
  );
};

// A row is a change when it has a prior value that differs — except where the format says
// the row is an outcome, which has no meaningful "before" to render against.
const isChanged = (f) =>
  !isOutcomeFormat(resolveFieldFormat(f.field, f.format)) &&
  hasValue(f.before) &&
  JSON.stringify(f.before) !== JSON.stringify(f.after);

/**
 * The action text shown beside the Type badge ("Stop Sell", "Bulk Update", "Edit Promo", …).
 *
 * Taken from the event's own action, not from the body: a text body IS the summary
 * sentence, but a diff body has none, so keying off the body left the badge standing
 * alone with no action text on every drill-down that renders a Change Log — which is
 * now every ARI surface. Falls back to the sentence when the action has no label.
 */
const actionText = (summary, item) => {
  // Same text as the results grid's Action column (`actionDescription`).
  const label = actionDescription(summary?.action);
  if (label) return label;
  return item?.body?.type === "text"
    ? formatValue(item.body.value, "text")
    : null;
};

/**
 * Change Log rows. A changed field renders before → after; anything else is a single
 * value, coloured as an addition because on this screen a one-sided entry is something
 * that happened (a VCC re-pushed, a user created) rather than neutral context.
 */
const changeRows = (body) => {
  if (!body) return [];
  if (body.type === "text") {
    // The sentence already sits beside the Type badge; no Change Log row for it.
    return [];
  }
  return (body.fields || []).map((f, idx) => {
    const format = resolveFieldFormat(f.field, f.format);
    const key = f.field || idx;
    return isChanged(f) ? (
      <DiffRow
        key={key}
        label={f.label}
        before={renderValue(f.before, format, "removed")}
        after={renderValue(f.after, format, "added")}
      />
    ) : (
      <ValueRow
        key={key}
        label={f.label}
        value={renderValue(f.after ?? f.value, format, "added")}
        tone="added"
      />
    );
  });
};

const hasDiff = (body) =>
  body?.type === "diff" && (body.fields || []).some(isChanged);

/**
 * One activity's sections: the per-action context the drill-down returns ("Applied To")
 * followed by its changes ("Change Log"). The From/To headers appear only when at least
 * one row is an actual before/after, matching the design — a single-value Change Log
 * (Res Report's re-pushed VCC) has no columns to head.
 */
const ItemSections = ({ items }) => {
  const appliedTo = buildAggregatedHeaderRows(items);
  const fields = aggregateChangeFields(items);
  // changeRows still walks a body, so hand it the merged fields as one.
  const mergedBody =
    fields.length > 0 ? { type: "diff", fields } : items?.[0]?.body;
  const rows = changeRows(mergedBody);

  if (appliedTo.length === 0 && rows.length === 0) return null;

  return (
    <>
      {appliedTo.length > 0 && (
        <DetailSection title="Applied To">
          {appliedTo.map((r) => (
            <ValueRow
              key={r.key}
              label={r.label}
              value={renderValue(r.value, resolveFieldFormat(r.key, r.format))}
            />
          ))}
        </DetailSection>
      )}
      {rows.length > 0 && (
        <DetailSection
          title="Change Log"
          columns={hasDiff(mergedBody) ? ["From", "To"] : null}
        >
          {rows}
        </DetailSection>
      )}
    </>
  );
};

ItemSections.propTypes = { items: PropTypes.array };

const ActivityDetailsModal = ({
  open,
  loading,
  details,
  summary,
  area,
  onClose,
}) => {
  const items = details?.items || [];
  const action = actionText(summary, items[0]);
  // Type sits in the info grid with the badge, so it is built here where the area and
  // the first item's action sentence are both in hand. Kept independent of `summary`:
  // the sentence is the activity's own description, and dropping it when the caller
  // has no summary row would lose the only human-readable account of what happened.
  const typeRow =
    area || action
      ? [
          {
            label: "Type",
            value: (
              <span className="ua-info-grid__type">
                <AreaBadge area={area} />
                {action && (
                  <span className="ua-info-grid__type-text">{action}</span>
                )}
              </span>
            ),
          },
        ]
      : null;
  const infoRows = [
    ...(summary ? buildDetailInfoRows(summary) : []),
    ...(typeRow ? [typeRow] : []),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      className="activity-details-modal"
    >
      <Modal.Header>
        <Modal.Title>Activity Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Loader center content="Loading details…" />}
        {!loading && (
          <>
            {infoRows.length > 0 && <InfoGrid rows={infoRows} />}
            {items.length === 0 && (
              <p className="activity-details__empty">No details available.</p>
            )}
            {items.length > 0 && <ItemSections items={items} />}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

ActivityDetailsModal.propTypes = {
  open: PropTypes.bool,
  loading: PropTypes.bool,
  details: PropTypes.object,
  summary: PropTypes.object,
  area: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ActivityDetailsModal.defaultProps = {
  open: false,
  loading: false,
  details: null,
  summary: null,
  area: null,
};

export default ActivityDetailsModal;
